import { LocalisationHelper } from '@/helpers/LocalisationHelper';

const STORAGE_KEY = 'app.data.lastLoadedAt';
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const shouldKeepKey = (key) => {
  if (key === STORAGE_KEY) {
    return true;
  }
  return key.startsWith('auth.');
};

export default ({ app, store }) => {
  if (process.server) {
    return;
  }

  let isReloading = false;

  const updateTimestamp = () => {
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch (error) {
      console.error('[StaleDataDetector] Failed to update timestamp:', error);
    }
  };

  const isDataStale = () => {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      if (!value) {
        return true;
      }
      const timestamp = parseInt(value, 10);
      if (Number.isNaN(timestamp)) {
        return true;
      }
      return Date.now() - timestamp > STALE_THRESHOLD_MS;
    } catch (error) {
      console.error('[StaleDataDetector] Failed to evaluate staleness:', error);
      return true;
    }
  };

  const clearStaleCache = () => {
    try {
      const keysToRemove = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!shouldKeepKey(key)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error('[StaleDataDetector] Failed to clear cache:', error);
    }
  };

  const reloadCriticalData = async () => {
    if (isReloading) {
      return;
    }

    isReloading = true;
    console.info('[StaleDataDetector] Reloading stale data');

    const tasks = [];

    const helper = new LocalisationHelper(
      app.$http,
      store,
      app.$auth,
      app.i18n && app.i18n.locale ? app.i18n.locale : 'fr'
    );

    tasks.push(helper.setListCountries());
    tasks.push(store.dispatch('shop/menu/fetchMenu'));

    try {
      await Promise.all(tasks);
      updateTimestamp();
      console.info('[StaleDataDetector] Data reload completed');
    } catch (error) {
      console.error('[StaleDataDetector] Data reload failed:', error);
    } finally {
      isReloading = false;
    }
  };

  const checkDataFreshness = async () => {
    if (!isDataStale()) {
      return;
    }

    console.info('[StaleDataDetector] Stale data detected');
    clearStaleCache();
    await reloadCriticalData();
  };

  store.subscribe((mutation) => {
    if (
      mutation.type === 'shop/menu/setMenuData' ||
      mutation.type === 'shop/localisation/setCountries'
    ) {
      updateTimestamp();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkDataFreshness();
    }
  });

  checkDataFreshness();
};
